import { useState, useEffect, useCallback } from 'react';
import api from '../../../api/client';

export default function PlanningGridPage() {
  const [models,   setModels]   = useState([]);
  const [versions, setVersions] = useState([]);
  const [lines,    setLines]    = useState([]);
  const [drivers,  setDrivers]  = useState([]);
  const [modelId,  setModelId]  = useState('');
  const [versionId,setVersionId]= useState('');
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState(null);
  const [saving,   setSaving]   = useState(false);

  useEffect(() => {
    api.get('/planning/models').then(({ data }) => setModels(data.data?.items || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!modelId) { setVersions([]); return; }
    api.get(`/planning/versions?model_id=${modelId}`)
      .then(({ data }) => setVersions(data.data?.items || []))
      .catch(() => {});
    api.get(`/planning/drivers?model_id=${modelId}`)
      .then(({ data }) => setDrivers(data.data?.items || []))
      .catch(() => {});
  }, [modelId]);

  const loadLines = useCallback(async () => {
    if (!versionId) { setLines([]); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/planning/lines?version_id=${versionId}`);
      setLines(data.data?.items || []);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [versionId]);

  useEffect(() => { loadLines(); }, [loadLines]);

  async function handleCellEdit(lineId, period, value) {
    setSaving(true);
    try {
      await api.patch(`/planning/lines/${lineId}`, { amountCents: parseInt(value, 10) || 0 });
      await loadLines();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  }

  async function handleRecompute() {
    if (!versionId) return;
    setSaving(true);
    try {
      await api.post(`/planning/versions/${versionId}/recompute`);
      await loadLines();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  }

  // Group lines by name, columns = unique periods
  const periods = [...new Set(lines.map((l) => l.period))].sort();
  const lineNames = [...new Set(lines.map((l) => l.name))];
  const cellMap = new Map(lines.map((l) => [`${l.name}::${l.period}`, l]));

  return (
    <div data-testid="planning-grid-page" data-loading={loading ? 'true' : 'false'}>
      <h2>Planning Grid</h2>

      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <select
          data-testid="planning-grid-model-select"
          value={modelId}
          onChange={(e) => { setModelId(e.target.value); setVersionId(''); }}
        >
          <option value="">— select model —</option>
          {models.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
        </select>

        <select
          data-testid="planning-grid-version-select"
          value={versionId}
          onChange={(e) => setVersionId(e.target.value)}
          disabled={!modelId}
        >
          <option value="">— select version —</option>
          {versions.map((v) => <option key={v.id} value={v.id}>{v.name} ({v.scenario})</option>)}
        </select>

        <button
          data-testid="planning-grid-recompute"
          onClick={handleRecompute}
          disabled={!versionId || saving}
        >
          Recompute formulas
        </button>
      </div>

      {drivers.length > 0 && (
        <div style={{ marginBottom: '1rem', padding: '0.5rem', background: '#f5f5f5' }}>
          <strong>Drivers: </strong>
          {drivers.map((d) => (
            <span key={d.id} data-testid={`planning-driver-${d.key}`} style={{ marginRight: '1rem' }}>
              {d.label} = {d.formula ? `formula: ${d.formula}` : d.value}
            </span>
          ))}
        </div>
      )}

      {error && <p style={{ color: 'red' }}>{error}</p>}
      {saving && <p style={{ color: '#888' }}>Saving…</p>}

      {versionId && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '4px 8px' }}>Line</th>
                {periods.map((p) => <th key={p} style={{ padding: '4px 8px' }}>{p}</th>)}
              </tr>
            </thead>
            <tbody>
              {lineNames.map((name) => (
                <tr key={name}>
                  <td style={{ padding: '4px 8px', fontWeight: 500 }}>{name}</td>
                  {periods.map((period) => {
                    const cell = cellMap.get(`${name}::${period}`);
                    return (
                      <td key={period} style={{ padding: '2px' }}>
                        {cell ? (
                          <input
                            data-testid={`planning-cell-${cell.id}-${period}`}
                            type="number"
                            defaultValue={cell.amount_cents}
                            disabled={!!cell.formula}
                            title={cell.formula ? `Formula: ${cell.formula}` : ''}
                            style={{
                              width: '90px',
                              textAlign: 'right',
                              background: cell.formula ? '#f0f8ff' : '',
                            }}
                            onBlur={(e) => handleCellEdit(cell.id, period, e.target.value)}
                          />
                        ) : (
                          <span style={{ color: '#ccc' }}>—</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
              {!lineNames.length && (
                <tr><td colSpan={periods.length + 1}>No lines in this version.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
